import { ChatDotsIcon, CodeIcon, CursorClickIcon, FileIcon, type Icon, QuotesIcon } from '@phosphor-icons/react';

import type { Selection } from '../../stores/selection-store';

export interface SelectionKindPresentation {
  Icon: Icon;
  /**
   * What the user's words about a piece of context are called, named after the gesture that
   * attached it — the plan view offers "Comment on this", so on a passage they are comments.
   */
  said: 'comment' | 'note';
  /**
   * Whether the snippet is prose the user reads, or machine text they scan.
   *
   * Plan passages and quoted messages are markdown lifted out of a rendered document, so a `<pre>`
   * shows them as the source they were never meant to be read as — a wall of pipes and hashes.
   * Code and element markup are the opposite: their exact characters and indentation *are* the
   * content, and rendering them would destroy the thing being pointed at.
   */
  prose: boolean;
}

/**
 * How each kind of attached context presents itself — shared by the composer's staged chips and
 * the sent message's chips, so a piece of context looks the same before and after it is sent.
 */
export const SELECTION_KIND: Record<Selection['kind'], SelectionKindPresentation> = {
  // A speech bubble, not the plan's own blueprint: what is attached here is the comment written
  // against a passage, and the blueprint marks the document itself everywhere else.
  plan: { Icon: ChatDotsIcon, said: 'comment', prose: true },
  quote: { Icon: QuotesIcon, said: 'note', prose: true },
  element: { Icon: CursorClickIcon, said: 'note', prose: false },
  code: { Icon: CodeIcon, said: 'note', prose: false },
  file: { Icon: FileIcon, said: 'note', prose: false },
};
